// 符文图标（月亮塔罗）：22 个金色线描小图标，全部用代码画（SVG，透明底）。
// viewBox 0 0 48 48，描边 = currentColor（CSS 决定金色 / 变暗），线宽统一 2.2、圆头圆角。
// manifest 里 place.rune.icon 是这里的键（tools/validate.ts 会检查）。

const f = (v: number) => +v.toFixed(2);

/** n 角星的多边形路径（外半径 R、内半径 r，第一个尖朝上） */
function star(cx: number, cy: number, R: number, r: number, n = 5, rot = 0): string {
  const pts: string[] = [];
  for (let i = 0; i < n * 2; i++) {
    const a = -Math.PI / 2 + rot + (i * Math.PI) / n;
    const rad = i % 2 ? r : R;
    pts.push(`${f(cx + Math.cos(a) * rad)} ${f(cy + Math.sin(a) * rad)}`);
  }
  return `M${pts.join('L')}Z`;
}

/** 从圆心向外的放射短线 */
function rays(cx: number, cy: number, r0: number, r1: number, n: number, rot = 0): string {
  let d = '';
  for (let i = 0; i < n; i++) {
    const a = rot + (i * 2 * Math.PI) / n;
    d += `M${f(cx + Math.cos(a) * r0)} ${f(cy + Math.sin(a) * r0)}L${f(cx + Math.cos(a) * r1)} ${f(cy + Math.sin(a) * r1)}`;
  }
  return d;
}

const p = (d: string, extra = '') => `<path d="${d}"${extra}/>`;
const c = (cx: number, cy: number, r: number, extra = '') => `<circle cx="${cx}" cy="${cy}" r="${r}"${extra}/>`;
const dot = (cx: number, cy: number, r = 1.3) => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="currentColor" stroke="none"/>`;

/** 钥匙：沿 +y 方向（之后整体旋转），环在上、齿在下 */
const key = `${c(24, 9, 4.5)}${p('M24 13.5V40M24 33h5M24 38h4')}`;

/** 王冠：底边 + 三个尖 + 尖上的小珠 */
const crownPath = 'M12 34h24M13 34l-2.5-14 7.5 6 6-10 6 10 7.5-6-2.5 14';

/** 花环：沿椭圆一圈叶子（交替朝内 / 朝外）+ 底部蝴蝶结 */
function garland(): string {
  let out = '';
  const n = 12;
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2 + Math.PI / n;
    const x = 24 + Math.sin(t) * 13;
    const y = 22 - Math.cos(t) * 16;
    if (y > 33) continue; // 底部留给蝴蝶结
    const tan = (Math.atan2(Math.sin(t) * 16, Math.cos(t) * 13) * 180) / Math.PI;
    const tilt = i % 2 ? 35 : -35;
    out += `<ellipse cx="${f(x)}" cy="${f(y)}" rx="4.2" ry="1.9" transform="rotate(${f(tan + tilt)} ${f(x)} ${f(y)})"/>`;
  }
  return p('M17.4 35.6A13 16 0 1 1 30.6 35.6') + out + p('M24 38l-7-4.5v9zM24 38l7-4.5v9z') + c(24, 38, 1.7) + p('M22 40l-3 5M26 40l3 5');
}

export const RUNE_ICONS: Record<string, string> = {
  // 愚者：一朵白玫瑰（花头漩涡 + 茎 + 叶）
  'white-rose': c(24, 16, 9) + p('M24 16c-2.5 0-3.4-2.6-1.2-3.8 2.6-1.4 5.6.6 5 3.6-.7 3.4-5.4 4.6-8.4 2.4') + p('M24 25v17') + p('M24 34c-5.5.4-8.6-2.6-8.8-5.8 4.6-.2 8.2 1.8 8.8 5.8z'),
  // 魔术师：∞ 光环
  infinity: p('M24 25c-4-5.5-7.6-8-11.2-8a8 8 0 0 0 0 16c3.6 0 7.2-2.5 11.2-8s7.6-8 11.2-8a8 8 0 0 1 0 16c-3.6 0-7.2-2.5-11.2-8z') + dot(24, 10) + dot(15, 12, 1) + dot(33, 12, 1),
  // 女祭司：新月
  crescent: p('M30 7.5A17 17 0 1 0 41 34 13.5 13.5 0 1 1 30 7.5z') + dot(36, 15, 1.1),
  // 皇后：星冠（王冠 + 三颗小星）
  'star-crown': p(crownPath) + p('M12 39h24') + p(star(10.5, 15, 3.6, 1.6)) + p(star(24, 10, 4.4, 1.9)) + p(star(37.5, 15, 3.6, 1.6)),
  // 皇帝：公羊头（脸 + 两只卷角）
  ram: p('M19 15q5-3 10 0l-1 15q-1 6-4 6t-4-6z') + p('M19.5 16c-4-5-11.5-3.5-12 2.5-.4 4.6 4.6 6.6 7.4 4.2 2-1.8.6-5-1.8-4') + p('M28.5 16c4-5 11.5-3.5 12 2.5.4 4.6-4.6 6.6-7.4 4.2-2-1.8-.6-5 1.8-4') + dot(21.4, 22) + dot(26.6, 22),
  // 教皇：交叉的两把钥匙
  keys: `<g transform="rotate(-38 24 24)">${key}</g><g transform="rotate(38 24 24)">${key}</g>`,
  // 恋人：张开翅膀的天使（头 + 光环 + 身体 + 双翼）
  angel: c(24, 13, 4) + `<ellipse cx="24" cy="6.5" rx="5" ry="1.6"/>` + p('M21 19l-4 20h14l-4-20z') + p('M21 21C14 14 7 16 6.5 24c4-1.6 9-1.2 13.4 1.4') + p('M27 21c7-7 14-5 14.5 3-4-1.6-9-1.2-13.4 1.4'),
  // 战车：缀星的车篷（拱顶 + 垂幔 + 两根柱 + 一颗星）
  'star-canopy': p('M8 19Q24 5 40 19') + p('M8 19q4 4.5 8 0q4 4.5 8 0q4 4.5 8 0q4 4.5 8 0') + p('M10 21v19M38 21v19M7 40h34') + p(star(24, 30, 5, 2.2)) + dot(16, 13, 1.1) + dot(32, 13, 1.1),
  // 力量：狮子（鬃毛一圈尖 + 脸 + 鼻 + 嘴）
  lion: p(star(24, 24, 19, 14.5, 12)) + c(24, 25, 9.5) + p('M21.5 27.5h5l-2.5 2.5z') + p('M24 30v1.6M24 31.6c-1.4 1.6-3.4 1.6-4.4.4M24 31.6c1.4 1.6 3.4 1.6 4.4.4') + dot(20.5, 23) + dot(27.5, 23),
  // 隐士：星形灯笼（提环 + 灯框 + 六芒星 + 底座）
  lantern: c(24, 5.5, 2.4) + p('M24 8v3M16 11h16l3 6v14l-3 6H16l-3-6V17z') + p('M24 16.5l5.6 9.8H18.4zM24 30.5l-5.6-9.8h11.2z') + p('M20 41h8'),
  // 命运之轮：轮圈 + 轮毂 + 八根辐条
  wheel: c(24, 24, 17) + c(24, 24, 13.5) + c(24, 24, 4) + p(rays(24, 24, 4, 13.5, 8, Math.PI / 8)) + dot(24, 7, 1.2) + dot(24, 41, 1.2) + dot(7, 24, 1.2) + dot(41, 24, 1.2),
  // 正义：天平（立柱 + 横梁 + 两只秤盘）
  scales: c(24, 7, 2) + p('M24 9v31M17 41h14M9 13h30') + p('M11 13l-5 13M11 13l5 13M37 13l-5 13M37 13l5 13') + p('M5 26a6 4 0 0 0 12 0zM31 26a6 4 0 0 0 12 0z'),
  // 倒吊人：头后的光环（头 + 一圈光 + 光芒）
  halo: c(24, 17, 11.5) + c(24, 19.5, 5.5) + p('M11 43c1-7.5 6-11 13-11s12 3.5 13 11') + dot(24, 2.6, 1.1) + dot(9, 9, 1.1) + dot(39, 9, 1.1),
  // 死神：白玫瑰旗（旗杆 + 飘动的旗 + 旗上一朵玫瑰）
  'rose-banner': c(10, 5.5, 1.8) + p('M10 7.5V43') + p('M10 9c8-3 14 3 21 0s9-1 10 1v16c-1-2-3-3-10-1s-13-3-21 0') + c(25, 17.5, 4.2) + p('M25 17.5c-1.2 0-1.5-1.2-.5-1.7 1.2-.6 2.4.4 2 1.6'),
  // 节制：彩虹（三道弧 + 两滴水）
  rainbow: p('M6 34a18 18 0 0 1 36 0M11 34a13 13 0 0 1 26 0M16 34a8 8 0 0 1 16 0') + p('M10 39.5q1.2 1.8 0 3.2-1.2-1.4 0-3.2zM38 39.5q1.2 1.8 0 3.2-1.2-1.4 0-3.2z'),
  // 恶魔：两只心形气球（绳子收在一起）
  hearts: p('M17 25c-6-4.6-9.6-8-9.6-12a4.8 4.8 0 0 1 9.6-1 4.8 4.8 0 0 1 9.6 1c0 4-3.6 7.4-9.6 12z') + p('M32 30c-5.2-4-8.2-7-8.2-10.4a4.1 4.1 0 0 1 8.2-.9 4.1 4.1 0 0 1 8.2.9C40.2 23 37.2 26 32 30z') + p('M17 25l7 17M32 30l-8 12') + p('M22 42h4'),
  // 高塔：被闪电打落、斜着飞走的金冠（王冠 + 下落的线）
  'falling-crown': `<g transform="rotate(-24 24 27)">${p('M12 34h24M13 34l-2.5-13 7.5 5.5 6-9.5 6 9.5 7.5-5.5-2.5 13')}${dot(24, 13, 1.4)}${dot(10.5, 18.5, 1.2)}${dot(37.5, 18.5, 1.2)}</g>` + p('M30 4v5M36 6v5M42 9v5'),
  // 星星：一颗大星（八芒 + 小星点）
  'great-star': p(star(24, 24, 18, 4, 4)) + p(star(24, 24, 9, 3, 4, Math.PI / 4)) + p(star(9, 9, 3, 1, 4)) + p(star(40, 38, 3, 1, 4)) + dot(39, 10, 1.1) + dot(9, 39, 1.1),
  // 月亮：闭着眼睛的睡月（圆脸 + 两道闭眼弧 + 微笑）
  'sleeping-moon': c(24, 24, 15) + p('M15.5 22q3 2.6 6 0M26.5 22q3 2.6 6 0M20 30q4 3 8 0') + p(star(41, 8, 3, 1.1, 4)) + dot(6, 10, 1.1),
  // 太阳：太阳脸（圆脸 + 十二道光 + 笑脸）
  'sun-face': c(24, 24, 10.5) + p(rays(24, 24, 13.5, 19.5, 12)) + p('M19 30q5 4 10 0') + dot(20, 21.5) + dot(28, 21.5),
  // 审判：金号角（吹口 + 管 + 喇叭口 + 飘带）
  trumpet: `<g transform="rotate(-16 24 24)">${p('M5 21v8M5 23h22M5 27h22')}${p('M27 23c6-1 10-6 14-10v24c-4-4-8-9-14-10')}${p('M14 27q-1 7-6 10M18 27q1.5 6 6 9')}</g>`,
  // 世界：花环（椭圆环 + 叶子 + 底部蝴蝶结）
  garland: garland(),
};

export const RUNE_ICON_KEYS = Object.keys(RUNE_ICONS);

/** 符文图标的 SVG 元素（class 由调用方给，颜色走 currentColor） */
export function runeSvg(icon: string, cls = 'rune-icon'): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 48 48');
  svg.setAttribute('class', cls);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2.2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.innerHTML = RUNE_ICONS[icon] ?? RUNE_ICONS['great-star'];
  return svg;
}
